import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import { ProblemStateBadge } from "@/components/Primitives";
import { fmtDate } from "@/lib/time";
import { Link2 } from "lucide-react";

const PAGE = 50;

export default function Problems() {
  // The state vocabulary comes from its OWN endpoint, never a hardcoded array
  // -- psa.py owns the list, and a fourth frontend copy is exactly how the
  // ticket status vocabulary drifted before this branch.
  const [states, setStates] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [body, setBody] = useState({ items: [], total: 0, offset: 0, limit: PAGE });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/desk/problem-states").then((r) => setStates(asArray(r.data))).catch(() => setStates([]));
  }, []);

  const load = useCallback((offset = 0) => {
    setLoading(true);
    api.get("/problems", { params: { state: stateFilter || undefined, offset, limit: PAGE } })
      .then((r) => setBody(r.data && typeof r.data === "object" && Array.isArray(r.data.items)
        ? r.data : { items: [], total: 0, offset: 0, limit: PAGE }))
      .catch(() => setBody({ items: [], total: 0, offset: 0, limit: PAGE }))
      .finally(() => setLoading(false));
  }, [stateFilter]);

  // A new filter always starts back at page 1.
  useEffect(() => { load(0); }, [load]);

  const offset = body.offset || 0;
  const pages = Math.max(1, Math.ceil(body.total / PAGE));
  const page = Math.floor(offset / PAGE) + 1;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · Problem Management</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            Problems <span className="text-zinc-500">({body.total})</span>
          </h1>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
          data-testid="problem-state-filter"
          className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm min-w-0 max-w-[12rem] shrink">
          <option value="">All states</option>
          {states.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {loading ? <div className="text-zinc-500">Loading…</div>
          : body.items.length === 0
            ? <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-6 text-zinc-500 text-sm">No problems match.</div>
            : body.items.map((p) => (
                <Link key={p.id} to={`/problems/${p.id}`} className="block" data-testid={`problem-row-${p.id}`}>
                  <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-3 hover:bg-zinc-900/40 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-zinc-500 shrink-0">#{p.number}</span>
                        <span className="font-medium text-zinc-100 truncate">{p.title}</span>
                        <ProblemStateBadge state={p.state} states={states} />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-zinc-500 font-mono shrink-0">
                      <span className="flex items-center gap-1"><Link2 size={12} /> {(p.linked_ticket_ids || []).length} linked</span>
                      <span>{fmtDate(p.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button disabled={offset <= 0} data-testid="problems-page-prev"
            onClick={() => load(Math.max(0, offset - PAGE))}
            className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Previous</button>
          <span className="text-zinc-500 font-mono text-xs">Page {page} of {pages} · {body.total} problems</span>
          <button disabled={page >= pages} data-testid="problems-page-next"
            onClick={() => load(offset + PAGE)}
            className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
